import type { ChangeEvent, ReactNode } from 'react'
import { ShieldCheck } from 'lucide-react'
import type { OtpChannel, SendOtp, VerifyOtp } from '@/modules/auth/use-cases'
import { Button, Input, InputOTP, InputOTPGroup, InputOTPSlot } from '@/shared/ui/primitives'
import { AuthLayout } from './auth-layout'
import { formatResendLabel } from '@/shared/ui/hooks'
import { useOtpVerificationStep } from './use-otp-verification-step'

export type OtpVerificationStepProps = {
  identifier: string
  channel: OtpChannel
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onVerifySuccess?: (accessToken: string) => Promise<void> | void
  onBack: () => void
  variant?: 'standalone' | 'inline'
}

export function OtpVerificationStep(props: OtpVerificationStepProps) {
  const vm = useOtpVerificationStep(props)
  const isEmail = props.channel === 'email'
  const variant = props.variant ?? 'standalone'

  const title = isEmail ? 'Check your email' : 'Check your phone'
  const subtitle = `We've sent a 6-digit code to ${vm.activeIdentifier}`

  const body = (
    <div className="flex flex-col gap-4">
      <InputOTP
        maxLength={6}
        value={vm.code}
        onChange={vm.setCode}
        onComplete={vm.handleVerify}
        containerClassName="w-full"
        autoFocus
      >
        <InputOTPGroup className="w-full gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot
              key={i}
              index={i}
              className="flex-1 h-14 text-xl font-semibold rounded-md border-l"
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      {vm.hasError ? (
        <p role="alert" className="text-sm text-destructive">
          {vm.errorMessage}
        </p>
      ) : null}
      <div className="flex items-start gap-2 rounded-md bg-muted px-3 py-2.5 text-xs text-muted-foreground">
        <ShieldCheck className="size-3.5 shrink-0 mt-0.5" aria-hidden />
        <span>For your security, this code expires in 5 minutes. Check your spam folder if you don&apos;t see it.</span>
      </div>
      {isEmail && vm.isEditingIdentifier ? (
        <div data-testid="otp-change-email-form" className="flex flex-col gap-2">
          <Input
            id="otp-change-email"
            type="email"
            aria-label="New email"
            value={vm.draftIdentifier}
            onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setDraftIdentifier(e.target.value)}
            placeholder="New email"
            autoComplete="email"
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={vm.cancelEditIdentifier}
              className="flex-1"
              disabled={vm.isResendingToNew}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void vm.submitNewIdentifier()
              }}
              disabled={!vm.canSubmitNewIdentifier || vm.isResendingToNew}
              className="flex-1"
              data-testid="otp-change-email-submit"
            >
              {vm.isResendingToNew
                ? 'Sending…'
                : vm.newIdentifierCooldownSeconds > 0
                  ? formatResendLabel(vm.newIdentifierCooldownSeconds)
                  : 'Resend to new email'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={vm.resendDisabled}
          onClick={vm.handleResend}
          className="w-full"
        >
          {vm.resendSeconds > 0 ? vm.resendLabel : 'Resend code'}
        </Button>
      )}
      {isEmail && !vm.isEditingIdentifier ? (
        <button
          type="button"
          onClick={vm.startEditIdentifier}
          data-testid="otp-change-email-toggle"
          className="text-sm text-muted-foreground hover:text-foreground text-center"
        >
          Change email
        </button>
      ) : null}
      <button
        type="button"
        onClick={props.onBack}
        className="text-sm text-muted-foreground hover:text-foreground text-center"
      >
        Use a different email
      </button>
    </div>
  )

  if (variant === 'inline') {
    return (
      <section data-testid="step-auth-otp" className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold leading-tight text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {body}
      </section>
    )
  }

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      {body as ReactNode}
    </AuthLayout>
  )
}
